import { highpass, leakyIntegrate, normalize, removeDC, riaaRecording } from "../dsp/filters";
import type { Values } from "../params";
import type { AudioSource } from "./source";

/**
 * Reproduce what wavtotext.py + Processing's loadStrings/float(split()) hand to the original sketches:
 * left channel only, 16-bit integers, -32768 turned into 0, and an extra NaN at the end (the text file
 * ends with a trailing comma, and float("") is NaN in Processing).
 */
export function prepOriginal(src: AudioSource): Float32Array {
  const L = src.channels[0];
  const out = new Float32Array(L.length + 1);
  for (let i = 0; i < L.length; i++) {
    let v = Math.round(Math.max(-1, Math.min(1, L[i])) * 32768);
    if (v > 32767) v = 32767;
    if (v === -32768) v = 0;
    out[i] = v;
  }
  out[L.length] = NaN;
  return out;
}

/** Text of the .txt file wavtotext.py would write (for "export like the original"). */
export function originalText(samples: Float32Array): string {
  const parts: string[] = [];
  for (let i = 0; i < samples.length; i++) if (!Number.isNaN(samples[i])) parts.push(String(samples[i]));
  return parts.join(",") + ",";
}

/** Improved audio prep. Returns mono float in [-1, 1] (peak-normalized) at src.sampleRate. */
export function prepImproved(src: AudioSource, v: Values): Float32Array {
  const [L, R = L] = src.channels;
  const n = L.length;
  const x = new Float32Array(n);
  switch (v.channel) {
    case "left": x.set(L); break;
    case "right": x.set(R); break;
    case "side": for (let i = 0; i < n; i++) x[i] = (L[i] - R[i]) / 2; break;
    default: for (let i = 0; i < n; i++) x[i] = (L[i] + R[i]) / 2;
  }
  const fs = src.sampleRate;
  removeDC(x);
  const hpf = Number(v.hpf ?? 0);
  if (hpf > 0) {
    // 4th order: two cascaded 2nd-order sections.
    highpass(hpf, fs).process(x);
    highpass(hpf, fs).process(x);
  }
  normalize(x);
  const drive = Math.pow(10, Number(v.driveDb ?? 0) / 20);
  if (v.limiter) {
    for (let i = 0; i < n; i++) x[i] = Math.tanh(x[i] * drive);
  } else if (drive !== 1) {
    for (let i = 0; i < n; i++) x[i] = Math.max(-1, Math.min(1, x[i] * drive));
  }
  if (v.cartridge === "magnetic") {
    // Magnetic cartridge reads velocity, phono preamp applies RIAA de-emphasis.
    // So: pre-emphasise, then integrate velocity → displacement (what we cut).
    riaaRecording(fs).process(x);
    leakyIntegrate(x, fs, 10);
    highpass(10, fs).process(x);
  }
  return normalize(x);
}
